import React from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {WeatherService} from '../api';
import {Location, WeatherForecast} from '../api/types';
import {
  EMPTY_FORECAST,
  pushLocationForecast,
  setLocationForecastModels,
} from '../store';
import {
  getForecastModelsByLocationHash,
  getSelectedForecastModelByLocationHash,
  getForecastSelectionByLocationHash,
} from '../store/location/selectors';
import {
  getLocationForecastData,
  getRawLocationForecastData,
} from '../store/weatherData/selectors';
import {Dates} from '../utils';
import {NormalizedForecastModel} from '../utils/Forecast/types';
import {useUserUnits} from './userContext';
import {lastModelListFetch, recordModelListFetch} from './modelListFetches';
import useApiClient, {isThrottled} from './useApiClient';
import useParamSelector from './useParamSelector';

// Per-session fetch bookkeeping, keyed by locationHash (`modelListFetches`,
// shared with useMissingPlaceForecastModels). See useStationForecast for the
// TTL rationale. We also remember the coordinates each hash was last fetched
// for: the current-GPS location is stored under a constant 'userLocation'
// hash (built by MyWeather.userLocationFromFix in both apps), so
// when the user travels the hash stays the same while lat/lon change. A
// hash-only TTL would suppress the refetch and keep showing the previous
// location's models (e.g. AROME while physically in Indonesia), so a
// coordinate change forces a refetch.
const FETCH_TTL_MS = 60_000;

/**
 * The outcome of a forecast request, as `useStationForecast` reports it, and
 * `throttled`: failed, refused for too many requests (a 429, FE #427), which
 * the webapp's place page says.
 */
export type LocationForecastOutcome =
  'fetched' | 'failed' | 'throttled' | 'skipped';

const useLocationForecast = (location: Location) => {
  const {fetch, loading: loadingForecast} = useApiClient();
  const units = useUserUnits();
  const dispatch = useDispatch();

  const locationHash = location?.hash;
  const locationLat = location?.coordinates?.latitude;
  const locationLon = location?.coordinates?.longitude;
  const locationAlt = location?.coordinates?.altitude;
  const locationTimezone = location?.timezone;

  const forecastModelList: NormalizedForecastModel[] = useParamSelector(
    getForecastModelsByLocationHash,
    locationHash,
  );

  const forecastModel: NormalizedForecastModel = useParamSelector(
    getSelectedForecastModelByLocationHash,
    locationHash,
  );

  // Why that model, not just which — see deriveForecastSelection.
  // `locationHash`, not `location.hash`: `getSelectedLocation` returns null
  // when there is no user location and no favourites, and both callers pass
  // that straight in. tsc cannot see it — the prop is typed `Location`.
  const forecastSelection = useParamSelector(
    getForecastSelectionByLocationHash,
    locationHash,
  );

  const forecastData: WeatherForecast = useParamSelector(
    getLocationForecastData,
    locationHash,
  );

  const fullForecastData = useSelector(getRawLocationForecastData);

  const getForecastDataForModel = React.useCallback(
    (model: NormalizedForecastModel) => {
      const key = model
        ? model.serverId.meshSize
          ? `${locationHash}-${model.serverId.forecastModel}-${model.serverId.meshSize}`
          : Object.keys(fullForecastData).find((k) =>
              k.startsWith(`${locationHash}-${model.serverId.forecastModel}-`),
            )
        : Object.keys(fullForecastData).find((k) =>
            k.startsWith(`${locationHash}`),
          );
      if (!key || !(key in fullForecastData)) {
        return EMPTY_FORECAST;
      } else {
        return fullForecastData[key];
      }
    },
    [fullForecastData, locationHash],
  );

  const isUpdateRequired = React.useCallback(
    (data?: WeatherForecast) => {
      const value = data || forecastData;
      if (
        value.computed_at === null ||
        Dates.timeDiffUtcWithNowInMin(value.computed_at) > 30
      ) {
        return true;
      }
      return false;
    },
    [forecastData],
  );

  const updateLocationForecastModels = React.useCallback(() => {
    if (locationLat != null && locationLon != null) {
      recordModelListFetch(locationHash, locationLat, locationLon);
      fetch(
        WeatherService.locationWeatherForecastModelList(
          locationLat,
          locationLon,
        ),
        {
          ignore404: true,
        },
      )
        .then((data) => {
          dispatch(
            setLocationForecastModels({
              hash: locationHash,
              forecastModels: data,
            }),
          );
        })
        .catch(() => {});
    }
  }, [dispatch, fetch, locationHash, locationLat, locationLon]);

  // Refresh periodically — see useStationForecast for the rationale — but also
  // whenever the coordinates for this hash change (the 'userLocation' hash is
  // reused as the GPS point moves), so a moved location never shows the previous
  // point's model list.
  React.useEffect(() => {
    if (!locationHash || locationLat == null || locationLon == null) return;
    const last = lastModelListFetch(locationHash);
    // Exact equality is safe because the 'userLocation' GPS point is rounded to
    // 3 decimals (~110 m) by MyWeather.userLocationFromFix, every fix in both
    // apps, and read once via getCurrentPosition, so
    // sub-100 m jitter never mutates lat/lon. If that
    // ever becomes a live watch on raw floats, switch to an epsilon compare —
    // otherwise every jittered fix would bypass the TTL and refetch the models.
    const coordinatesChanged =
      !last || last.lat !== locationLat || last.lon !== locationLon;
    const ttlExpired = last != null && Date.now() - last.at > FETCH_TTL_MS;
    if (coordinatesChanged || ttlExpired) {
      updateLocationForecastModels();
    }
  }, [locationHash, locationLat, locationLon, updateLocationForecastModels]);

  // What became of the request, as the station hook reports it (FE #381
  // W4): a screen can tell a forecast on its way from one that failed.
  const updateGivenForecastModel = React.useCallback(
    (model: NormalizedForecastModel): Promise<LocationForecastOutcome> => {
      // A point on the equator or the meridian is a point: only none skips.
      if (locationHash && locationLat != null && locationLon != null) {
        return fetch(
          WeatherService.locationWeatherForecast({
            lat: locationLat,
            lon: locationLon,
            elevation: locationAlt,
            timezone: locationTimezone,
            forecastModel: model ? model.serverId.forecastModel : undefined,
            meshSize: model ? model.serverId.meshSize : undefined,
          }),
          // The daily forecast description is formatted server-side, so the
          // backend needs the user's units. Authenticated users are resolved
          // from their profile, but anonymous viewers are only conveyed via
          // this header — without it the description falls back to metric.
          {
            ignore404: true,
            headers: {'X-SmartMixin-Units': JSON.stringify(units)},
          },
        )
          .then((data: WeatherForecast): LocationForecastOutcome => {
            dispatch(
              pushLocationForecast({
                locationHash: locationHash,
                forecastModel: data.metadata.forecast_model,
                meshSize: data.metadata.mesh_size,
                data,
              }),
            );
            return 'fetched';
          })
          .catch((error: unknown): LocationForecastOutcome =>
            isThrottled(error) ? 'throttled' : 'failed',
          );
      }
      return Promise.resolve('skipped');
    },
    [
      fetch,
      units,
      locationHash,
      locationLat,
      locationLon,
      locationAlt,
      locationTimezone,
      dispatch,
    ],
  );

  const updateLocationForecast = React.useCallback(
    () => updateGivenForecastModel(forecastModel),
    [forecastModel, updateGivenForecastModel],
  );

  const result = React.useMemo(
    () => ({
      updateLocationForecast,
      updateLocationForecastModels,
      forecastModelList,
      forecastModel,
      forecastSelection,
      forecastData,
      isUpdateRequired,
      getForecastDataForModel,
      updateGivenForecastModel,
      loadingForecast,
    }),
    [
      updateLocationForecast,
      updateLocationForecastModels,
      forecastModelList,
      forecastModel,
      forecastSelection,
      forecastData,
      isUpdateRequired,
      getForecastDataForModel,
      updateGivenForecastModel,
      loadingForecast,
    ],
  );

  return result;
};

export default useLocationForecast;
